/**
 * Parses Cloudinary dimension string (e.g., 'w_300,h_300' or 'w_300') into structured parameters
 */
function parseCloudinaryDimensions(dimensions: string): { width?: number; height?: number; quality?: number; mode?: string } {
    if (!dimensions) {
        return {};
    }

    const params: { width?: number; height?: number; quality?: number; mode?: string } = {};
    const parts = dimensions.split(',');

    parts.forEach(part => {
        const trimmed = part.trim();
        if (trimmed.startsWith('w_')) {
            const width = parseInt(trimmed.substring(2), 10);
            if (!isNaN(width)) {
                params.width = width;
            }
        } else if (trimmed.startsWith('h_')) {
            const height = parseInt(trimmed.substring(2), 10);
            if (!isNaN(height)) {
                params.height = height;
            }
        } else if (trimmed.startsWith('q_')) {
            const quality = parseInt(trimmed.substring(2), 10);
            if (!isNaN(quality)) {
                params.quality = quality;
            }
        } else if (trimmed.startsWith('c_')) {
            params.mode = trimmed.substring(2);
        }
    });

    return params;
}

/**
 * Transforms image URLs for both Cloudinary and Cloudflare R2
 * @param link - Image URL to transform
 * @param quality - Image quality (1-100)
 * @param width - Image width in pixels
 * @param height - Image height in pixels
 * @param mode - Transformation mode ('scale', 'fill', etc.)
 * @returns Transformed image URL
 */
export function transformImage(link: string, quality?: number, width?: number, height?: number, mode?: string): string {
    if (!link || link === 'default') {
        return link;
    }

    // Only apply Cloudflare R2 image resizing for assets.staffy.com URLs
    if (!link.includes('assets.staffy.com')) {
        return link;
    }

    const params: string[] = [];

    if (Number.isInteger(width)) {
        params.push(`width=${width}`);
    }
    if (Number.isInteger(height)) {
        params.push(`height=${height}`);
    }
    if (Number.isInteger(quality)) {
        params.push(`quality=${quality}`);
    }

    let fitMode = 'scale-down';
    if (mode === 'fill') {
        fitMode = 'cover';
    }
    params.push(`fit=${fitMode}`);
    params.push('format=webp');

    try {
        const url = new URL(link);
        return `${url.origin}/cdn-cgi/image/${params.join(',')}${url.pathname}`;
    } catch (e) {
        console.error('Failed to parse image URL:', link, e);
        return link;
    }
}

/**
 * Transforms image URL using Cloudinary-style dimension string (e.g., 'w_300,h_300')
 * Automatically detects if URL is Cloudinary or Cloudflare R2 and applies appropriate transformations
 * @param imageUrl - Image URL to transform
 * @param dimensions - Cloudinary-style dimension string (e.g., 'w_300,h_300', 'w_250,h_200')
 * @returns Transformed image URL
 */
export function transformImageWithDimensions(imageUrl: string, dimensions?: string): string {
    if (!imageUrl || imageUrl === 'default') {
        return 'assets/images/staffy-gray.png';
    }

    // Parse Cloudinary dimension string into structured parameters
    const params = parseCloudinaryDimensions(dimensions || '');

    // Apply transformations using the main transform function
    return transformImage(imageUrl, params.quality, params.width, params.height, params.mode);
}

export function dataURLtoFile(dataurl: string, filename: string) {
    const arr = dataurl.split(','),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
}

/**
 * Detects file extension from URL by searching anywhere in the path
 * Handles both standard URLs and migrated R2 URLs where extension may not be at the end
 * @param url - URL to check
 * @returns File extension (lowercase, without dot) or null if not found
 */
export function getFileExtensionFromUrl(url: string): string | null {
    if (!url) {
        return null;
    }

    // Clean up URL - remove query params and hash
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Check for common file extensions anywhere in the URL path
    const extensionPattern = /\.(pdf|docx|doc|txt|jpg|jpeg|png|gif|bmp|webp|heic|heif)(?=[\/\d\.]|$)/gi;
    const matches = cleanUrl.match(extensionPattern);

    if (matches && matches.length > 0) {
        // Return the last matched extension (most likely the actual file extension)
        const lastMatch = matches[matches.length - 1];
        return lastMatch.substring(1).toLowerCase(); // Remove the dot and convert to lowercase
    }

    return null;
}

/**
 * Determines certificate file type from URL or originalCloudinaryUrl
 * @param url - Current URL (possibly migrated R2 URL)
 * @param originalCloudinaryUrl - Original Cloudinary URL before migration (optional)
 * @returns Object with file type flags
 */
export function detectCertificateFileType(url: string, originalCloudinaryUrl?: string): {
    isPDF: boolean;
    isDoc: boolean;
    isImage: boolean;
    extension: string | null;
} {
    // Prefer originalCloudinaryUrl for file type detection if available
    const urlToCheck = originalCloudinaryUrl || url;
    const extension = getFileExtensionFromUrl(urlToCheck);

    const isPDF = extension === 'pdf';
    const isDoc = extension === 'docx' || extension === 'doc' || extension === 'txt';
    const isImage = !isPDF && !isDoc && (
        extension === 'jpg' ||
        extension === 'jpeg' ||
        extension === 'png' ||
        extension === 'gif' ||
        extension === 'bmp' ||
        extension === 'webp' ||
        extension === 'heic' ||
        extension === 'heif'
    );

    return {
        isPDF,
        isDoc,
        isImage: isImage || (!isPDF && !isDoc), // Default to image if type cannot be determined
        extension
    };
}

export function isImageUrl(url: string): boolean {
    if (!url) {
        return false;
    }
    const extension = getFileExtensionFromUrl(url);
    return ['jpeg', 'jpg', 'png', 'gif', 'bmp', 'webp', 'heic', 'heif'].includes(extension || '');
}

export async function base64ToFile(base64String: string, fileName: string): Promise<File> {
    let file = await fetch(base64String);
    const blob = await file.blob();
    return new File([blob], fileName, { type: 'image/png' });
}
